<template>
	<section v-if="loaded">
		<header class="section-title">
			<h3>Affiliate Links</h3>
			<a @click.prevent="showCreateModal" class="btn btn-md btn-success" href="#" v-if="userRoles.includes('affiliate_links.create')"><i class="fas fa-plus" title="Add"></i> New Affiliate Link</a>
		</header>
		<div class="table-reponsive">
			<table>
				<thead>
					<tr>
						<th>Affiliate Tag</th>
						<th>Status</th>
						<th>Language</th>
						<th>Position</th>
						<th>Last Checked</th>
						<th>Actions</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="affiliateLink in affiliateLinks">
						<td>{{ affiliateLink.tag }}</td>
						<td>
							<span class="badge badge-primary" v-if="affiliateLink.isDefault"><i class="fas fa-check"></i> Default</span>
							<span class="badge badge-success" v-if="affiliateLink.isWorking"><i class="fas fa-check"></i> Working</span>
							<span class="badge badge-danger" v-else><i class="fas fa-exclamation-triangle"></i> Not working</span>
						</td>
						<td>{{ affiliateLink.language }}</td>
						<td>{{ affiliateLink.visiblePosition }}</td>
						<td>{{ affiliateLink.lastModified }}</td>
						<td>
							<a :disabled="affiliateLink.isDefault || !userRoles.includes('affiliate_links.update')" @click.prevent="setDefault(affiliateLink.id)" class="btn btn-icon btn-success" href="#" title="Make default"><i class="fas fa-check"></i></a>
							<a @click.prevent="showEditModal(affiliateLink)" class="btn btn-icon btn-primary" href="#" title="Edit" v-if="userRoles.includes('affiliate_links.update')"><i class="fas fa-pencil-alt"></i></a>
							<a :disabled="affiliateLink.isDefault || !userRoles.includes('affiliate_links.delete')" @click.prevent="deleteAffiliateLink(affiliateLink.id)" class="btn btn-icon btn-danger" href="#" title="Delete"><i class="fas fa-trash-alt"></i></a>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<div class="modal-bg" v-if="isModalVisible">
			<div class="modal">
				<header class="modal-header">
					<h4 class="modal-title" v-if="selectedAffiliateLink.id !== null">Edit Affiliate Link</h4>
					<h4 class="modal-title" v-else>New Affiliate Link</h4>
					<a @click="closeModal" class="modal-close" title="Close modal"><i class="fas fa-times"></i></a>
				</header>
				<section class="modal-body">
					<div class="flex box start">
						<div class="form-group four-lg">
							<label for="affiliate-tag">Affiliate Tag</label>
							<input id="affiliate-tag" type="text" v-model="selectedAffiliateLink.tag">
							<p class="input-error" v-if="errors.errors.tag">{{ errors.errors.tag }}</p>
						</div>
						<div class="form-group four-lg">
							<label>Position</label>
							<div class="flex box center">
								<label class="label-checkbox five-lg">
									<input type="checkbox" v-model="selectedAffiliateLink.anyPosition">
									<span>Any position</span>
									<p class="input-error" v-if="errors.errors.anyPosition">{{ errors.errors.anyPosition }}</p>
								</label>
								<div class="position seven-lg">
									<span class="position-or">or</span>
									<input :disabled="selectedAffiliateLink.anyPosition" id="position" min="1" type="number" v-model="selectedAffiliateLink.position">
									<p class="input-error" v-if="errors.errors.position">{{ errors.errors.position }}</p>
								</div>
							</div>
						</div>
						<div class="form-group four-lg">
							<label>Language</label>
							<select v-model="selectedAffiliateLink.language_id">
								<option :value="language.id" v-for="language in availableLanguages">{{ language.title }}</option>
							</select>
							<p class="input-error" v-if="errors.errors.language_id">{{ errors.errors.language_id }}</p>
						</div>
					</div>
					<div class="form-group">
						<label for="url">URL</label>
						<div class="flex box">
							<div class="input-group nine-lg">
								<input id="url" type="url" v-model="selectedAffiliateLink.link">
								<a @click.prevent="testUrl" class="btn btn-primary" href="#">Test URL</a>
							</div>
							<a @click.prevent="addParams" class="btn btn-primary three-lg" href="#">Add default params</a>
						</div>
						<p class="input-error" v-if="errors.errors.link">{{ errors.errors.link }}</p>
					</div>
					<h3>Checklist</h3>
					<table>
						<thead>
							<th>Condition</th>
							<th>Status</th>
						</thead>
						<tbody>
							<tr>
								<td>The URL entered is valid and contains the required
									<code class="code-inline">_PARAM1_</code> parameter for tracking purposes
								</td>
								<td v-if="containsParam">
									<i class="fas fa-fw fa-check text-success fa-2x" title="Success"></i></td>
								<td v-else><i class="fas fa-fw fa-times text-danger fa-2x" title="No-no!"></i></td>
							</tr>
							<tr>
								<td>The URL is live and working</td>
								<td v-if="selectedAffiliateLink.isWorking">
									<i class="fas fa-fw fa-check text-success fa-2x" title="Success"></i></td>
								<td v-else><i class="fas fa-fw fa-times text-danger fa-2x" title="No-no!"></i></td>
							</tr>
							<tr>
								<td>
									I, {{ $currentUser.full_name }} have checked that the affiliate link is working as intended
									<input class="checkbox-confirmation" type="checkbox" v-model="userCheck">
								</td>
								<td v-if="userCheck">
									<i class="fas fa-fw fa-check text-success fa-2x" title="Success"></i></td>
								<td v-else><i class="fas fa-fw fa-times text-danger fa-2x" title="No-no!"></i></td>
							</tr>
						</tbody>
					</table>
				</section>
				<footer class="modal-footer">
					<a @click.prevent="closeModal" class="btn btn-md btn-primary modal-close" href="#"><i class="fas fa-times"></i> Cancel</a>
					<a :disabled="!(containsParam && userCheck)" @click="saveAffiliateLink" class="btn btn-md btn-success" href="#"><i class="fas fa-plus"></i> Add Affiliate Link</a>
				</footer>
			</div>
		</div>
	</section>
</template>

<script>
	import Errors from '../../Utils/Errors.js';

	export default {
		props: {
			providerid: {
				type: Number
			}
		},
		data() {
			return {
				tabName: 'affiliateLinks',
				loaded: false,
				providerId: this.providerid,
				provider: {},
				affiliateLinks: {},

				userRoles: this.$currentUserRoles,
				availableLanguages: this.$availableLanguages,

				selectedAffiliateLink: {
					id: null,
					tag: null,
					link: null,
					anyPosition: true,
					position: 0,
					language_id: 0,
					isWorking: false
				},
				userCheck: false,
				isModalVisible: false,

				errors: new Errors()
			}
		},
		computed: {
			containsParam: function () {
				return !!(this.selectedAffiliateLink &&
					this.selectedAffiliateLink.link !== null &&
					this.selectedAffiliateLink.link.includes('_PARAM1_'));

			}
		},
		created() {
			this.fetchDataFromApi();
		},
		methods: {
			fetchDataFromApi: function () {
				let vm = this;
				axios.post('/api/' + vm.$selectedSite.slug + '/providers/fetchtabdata/' + vm.providerId, {
					tab: vm.tabName
				})
					.then(response => {
						if (response.data.hasOwnProperty('provider')) {
							vm.provider = response.data.provider;
						}
						if (response.data.hasOwnProperty('affiliateLinks')) {
							vm.affiliateLinks = response.data.affiliateLinks;
						}
						vm.loaded = true;
					})
					.catch(e => {
						vm.$toastr.error('Api ' + vm.tabName + ' tab load failed', 'Error');
					});
			},
			showCreateModal: function () {
				this.selectedAffiliateLink = {
					id: null,
					tag: null,
					link: null,
					anyPosition: true,
					position: 0,
					language_id: 0,
					isWorking: false
				};
				this.userCheck = false;
				this.isModalVisible = true;
			},
			showEditModal: function (affiliateLink) {
				this.selectedAffiliateLink = {
					id: affiliateLink.id,
					tag: affiliateLink.tag,
					link: affiliateLink.link,
					anyPosition: affiliateLink.position === 0,
					position: affiliateLink.position,
					language_id: affiliateLink.language_id,
					isWorking: affiliateLink.isWorking
				};
				this.userCheck = false;
				this.isModalVisible = true;
			},
			closeModal: function () {
				this.isModalVisible = false;
			},
			saveAffiliateLink: function () {
				let vm = this;

				if (vm.selectedAffiliateLink.id) {
					axios.put('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/affiliate-links/' + vm.selectedAffiliateLink.id, vm.selectedAffiliateLink)
						.then(response => {
							this.$toastr.success('Affiliate Link updated successfully.');
							window.location.reload();
						})
						.catch(e => {
							this.errors.record(e.response.data);
							this.$toastr.error('Affiliate Link update failed.', 'Error');
						});
				} else {
					axios.post('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/affiliate-links', vm.selectedAffiliateLink)
						.then(response => {
							this.$toastr.success('Affiliate Link added successfully.');
							window.location.reload();
						})
						.catch(e => {
							this.errors.record(e.response.data);
							this.$toastr.error('Save new Affiliate Link failed.', 'Error');
						});
				}
			},
			deleteAffiliateLink: function (id) {
				let vm = this;

				this.$dialog.confirm('Are you sure you want to delete this Affiliate Link?', {
					loader: true
				})
					.then((dialog) => {
						axios.delete('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/affiliate-links/' + id)
							.then(response => {
								this.$toastr.success('Affiliate Link deleted successfully.');
								window.location.reload();
							})
							.catch(e => {
								this.errors.record(e.response.data);
								this.$toastr.error('Affiliate Link delete failed.', 'Error');
							});
					})
					.catch(() => {
						this.$toastr.error('Delete aborted.');
					});
			},
			testUrl: function () {
				let vm = this;

				axios.post('/api/' + vm.$selectedSite.slug + '/affiliateLinkTester', {url: vm.selectedAffiliateLink.link})
					.then(response => {
						if (response.data.success) {
							vm.selectedAffiliateLink.isWorking = true;
							this.$toastr.success('The given link is working.');
						} else {
							vm.selectedAffiliateLink.isWorking = false;
							this.$toastr.error('The given link isn\'t working.', 'Error');
						}
					})
					.catch(e => {
						vm.selectedAffiliateLink.isWorking = false;
						this.$toastr.error('The given link isn\'t working.', 'Error');
					});
			},
			setDefault: function (id) {
				let vm = this;

				axios.post('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/affiliate-links/' + id + '/setDefault')
					.then(response => {
						this.$toastr.success('Affiliate link set default successfully.');
						window.location.reload();
					})
					.catch(e => {
						this.$toastr.error('Affiliate link set default failed.', 'Error');
					});
			},
			addParams: function () {
				let vm = this;

				if (vm.selectedAffiliateLink.link && !vm.selectedAffiliateLink.link.includes('_PARAM1_')) {
					if (vm.selectedAffiliateLink.link.includes('?')) {
						vm.selectedAffiliateLink.link = vm.selectedAffiliateLink.link.concat('&data1=_PARAM1_&data2=_PARAM2_&data3=_PARAM3_&data4=_PARAM4_&data5=_PARAM5_');
					} else {
						vm.selectedAffiliateLink.link = vm.selectedAffiliateLink.link.concat('?data1=_PARAM1_&data2=_PARAM2_&data3=_PARAM3_&data4=_PARAM4_&data5=_PARAM5_');
					}
				}
			}
		}
	}
</script>

<style scoped>

</style>
